import { IGatsbyImageData } from 'gatsby-plugin-image'
import {
  formatContractType,
  formatPublishedDate,
  formatRangeForGoogleJobs,
  formatSalary,
  formatStartDate,
  getLastUpdateDate,
} from '../format'
import {
  addDomainOnUrl,
  returnNullIfEmptyString,
} from '../../../Utils/functionnals'
import { formatHoursPerWeek } from '../../../Utils/offers/hoursPerWeek'

export type Offer = {
  id: string | null
  title: string | null
  slug: string | null
  descriptions: {
    missions: string | null
    needs: string | null
    prospects: string | null
    workWithUs: string | null
    process: string | null
  }
  publishedDate: string | null
  positionFilled: boolean | null
  startDate: string
  educationRequirement: string | null
  experienceRequirement: string | null
  contractType: string | null
  hoursPerWeek: string | null
  fullyRemoteJob: string | null
  payRange: string | null
  office: {
    street: string | null
    postalCode: string | null
    cityForMap: string | null
    city: string | null
    lat: number | null
    lng: number | null
    firm: {
      slug: string | null
      name: string | null
      hasCareerSite: boolean | null
      colorCareerSite: string | null
      logo: string | IGatsbyImageData | null
      header: string | IGatsbyImageData | null
      offerMedias: string[] | IGatsbyImageData[] | null
    }
  }
  tools: offerTool[] | null
}

export type offerTool = {
  name: string | null
  slug: string | null
  logo: IGatsbyImageData | null
  logoUrl: string | null
  permalink: string | null
} | null

export function graphqlOfferMapper(data: Queries.OfferQuery): Offer | null {
  if (!data) {
    return null
  }
  let offer = null
  const offerDates = [
    data.strapiAccountingOffer?.publishedAt?.toString(),
    data.strapiAccountingOffer?.createdAt?.toString(),
    data.strapiAccountingOffer?.updatedAt?.toString(),
  ].filter((item) => item) as string[]
  const publishedDate = getLastUpdateDate(offerDates)
  const formattedPublishedDate = publishedDate
    ? formatPublishedDate(publishedDate)
    : null
  const cityName = data.strapiAccountingOffer?.office?.city ?? ''
  const formatedCity = `${cityName} ${
    data.strapiAccountingOffer?.office?.postalCode
      ? `(${data.strapiAccountingOffer?.office?.postalCode.slice(0, 2)})`
      : ''
  }`
  const startDate = data.strapiAccountingOffer?.startDate ?? null
  const startDateFormatted = formatStartDate(startDate)
  const min = data?.strapiAccountingOffer?.payRange?.min ?? undefined
  const max = data?.strapiAccountingOffer?.payRange?.max ?? undefined
  const payRangeFormatted = formatSalary({ min, max }) ?? null
  const contractType =
    typeof data.strapiAccountingOffer?.contractType === 'string'
      ? data.strapiAccountingOffer?.contractType
      : ''
  const contractTypeFormatted = formatContractType(contractType) ?? null

  const medias = data.strapiAccountingOffer?.office?.firm?.medias ?? null
  let offerMedias = null
  if (medias && medias.length > 0) {
    offerMedias = medias.map((media) => {
      if (media?.local?.localFile?.childImageSharp?.gatsbyImageData) {
        return media.local.localFile.childImageSharp.gatsbyImageData
      }
      return null
    }) as IGatsbyImageData[] | null

    offerMedias = offerMedias
      ? offerMedias.filter((media) => media !== null)
      : null
  }

  const tools = data.strapiAccountingOffer?.accountingTools ?? null
  let offerTools = null
  if (tools && tools.length > 0) {
    offerTools = tools.map((tool) => {
      if (
        tool &&
        (tool?.logo?.localFile?.childImageSharp?.gatsbyImageData ||
          tool?.logo?.localFile?.url)
      ) {
        const toolSlug = tool.permalink?.split('/').pop() || tool.permalink
        return {
          name: tool.name || null,
          slug: toolSlug,
          logo: tool.logo?.localFile?.childImageSharp?.gatsbyImageData || null,
          logoUrl: tool.logo?.localFile?.url || null,
          permalink: tool.permalink || null,
        }
      }
      return null
    })
  }

  const logoToDisplay = getOfferLogo(
    data?.strapiAccountingOffer?.office?.firm?.logo,
  )

  if (data.strapiAccountingOffer) {
    offer = {
      id: data.strapiAccountingOffer.uuid,
      title: data.strapiAccountingOffer.title,
      slug: data.strapiAccountingOffer.slug,
      descriptions: {
        missions: returnNullIfEmptyString(
          data.strapiAccountingOffer.description?.missions?.data?.missions,
        ),
        needs: returnNullIfEmptyString(
          data.strapiAccountingOffer.description?.needs?.data?.needs,
        ),
        prospects: returnNullIfEmptyString(
          data.strapiAccountingOffer.description?.prospects?.data?.prospects,
        ),
        workWithUs: returnNullIfEmptyString(
          data.strapiAccountingOffer.description?.workWithUs?.data?.workWithUs,
        ),
        process: returnNullIfEmptyString(
          data.strapiAccountingOffer.description?.process?.data?.process,
        ),
      },
      publishedDate: formattedPublishedDate,
      positionFilled: data.strapiAccountingOffer?.positionFilled || null,
      startDate: startDateFormatted,
      educationRequirement:
        data.strapiAccountingOffer.educationRequirement || null,
      experienceRequirement:
        data.strapiAccountingOffer.experienceRequirement || null,
      contractType: contractTypeFormatted || null,
      payRange: payRangeFormatted,
      fullyRemoteJob: data.strapiAccountingOffer.fullyRemoteJob
        ? 'Full remote'
        : null,
      hoursPerWeek: formatHoursPerWeek(
        data.strapiAccountingOffer.hoursPerWeek || null,
      ),
      office: {
        city: formatedCity,
        cityForMap: data.strapiAccountingOffer.office?.city || null,
        street: data.strapiAccountingOffer.office?.street || null,
        postalCode: data.strapiAccountingOffer.office?.postalCode || null,
        lat: data.strapiAccountingOffer.office?.latitude || null,
        lng: data.strapiAccountingOffer.office?.longitude || null,
        firm: {
          slug: data.strapiAccountingOffer.office?.firm?.slug || null,
          name: data.strapiAccountingOffer.office?.firm?.name || null,
          logo: logoToDisplay || null,
          hasCareerSite:
            data.strapiAccountingOffer.office?.firm?.hasCareerSite || null,
          colorCareerSite:
            data.strapiAccountingOffer.office?.firm?.colorCareerSite || null,
          header:
            data.strapiAccountingOffer.office?.firm?.header?.localFile
              ?.childImageSharp?.gatsbyImageData || null,
          offerMedias: offerMedias?.slice(0, 4) || null,
        },
      },
      tools: offerTools || null,
    }
  }

  return offer
}

type AccountingOfferLogo = {
  readonly localFile: {
    readonly extension: string
    readonly url: string | null
    readonly childImageSharp: {
      readonly gatsbyImageData: import('gatsby-plugin-image').IGatsbyImageData
    } | null
  } | null
}

export function getOfferLogo(
  logo?: AccountingOfferLogo | null,
): string | IGatsbyImageData | null {
  if (!logo) {
    return null
  }
  if (
    logo?.localFile?.extension &&
    ['svg', 'bmp'].includes(logo?.localFile?.extension)
  ) {
    return logo?.localFile?.url
  }
  if (logo?.localFile?.childImageSharp?.gatsbyImageData) {
    return logo?.localFile?.childImageSharp?.gatsbyImageData
  }
  return logo?.localFile?.url || null
}

export type fetchOffer = {
  attributes: {
    uuid: string | null
    title: string
    slug: string | null
    description: {
      id: number | null
      missions: string | null
      needs: string | null
      prospects: string | null
      workWithUs: string | null
      process: string | null
    }
    publishedAt: string | null
    fullyRemoteJob: boolean | null
    positionFilled: boolean | null
    startDate: string | null
    educationRequirement: string | null
    experienceRequirement: string | null
    contractType: string | null
    hoursPerWeek: number | null
    payRange: {
      min: number | null
      max: number | null
    } | null
    office: {
      data: {
        id: number | null
        attributes: {
          uuid: string | null
          name: string | null
          street: string | null
          city: string | null
          postalCode: string | null
          latitude: number | null
          longitude: number | null
          createdAt: string | null
          updatedAt: string | null
          firm: {
            data: {
              id: number | null
              attributes: {
                uuid: string | null
                foundedYear: number | null
                totalClients: string | null
                totalEmployees: string | null
                clienttDescription: string | null
                firmValues: string | null
                employeeAgeAverage: number | null
                description: string | null
                slug: string | null
                name: string | null
                website: string | null
                hasCareerSite: boolean | null
                colorCareerSite: string | null
                premium: boolean | null
                createdAt: string | null
                updatedAt: string | null
                logo: {
                  data: {
                    id: number | null
                    attributes: {
                      url: string | null
                    }
                  }
                } | null
                header: {
                  data: {
                    attributes: {
                      url: string | null
                    }
                  }
                } | null
                medias:
                  | {
                      url: string | null
                      local: {
                        data: {
                          attributes: {
                            url: string | null
                          }
                        } | null
                      } | null
                    }[]
                  | null
              }
            }
          } | null
        }
      }
    } | null
    accountingTools: {
      data:
        | {
            attributes: {
              name: string | null
              logo: {
                data: {
                  attributes: {
                    logo: {
                      data: {
                        attributes: {
                          formats: {
                            thumbnail: {
                              url: string | null
                            }
                          }
                        }
                      }
                    } | null
                    url: string | null
                  }
                }
              } | null
              permalink: string | null
            }
          }[]
        | null
    } | null
  }
}

export function fetchOfferMapper(data: fetchOffer): Offer | null {
  let offer = null
  const publishedDate = data.attributes?.publishedAt ?? null
  const formattedPublishedDate = publishedDate
    ? formatPublishedDate(publishedDate)
    : null
  const cityName = data.attributes?.office?.data?.attributes?.city ?? ''
  const formatedCity = `${cityName} ${
    data.attributes?.office?.data?.attributes?.postalCode
      ? `(${data.attributes?.office?.data?.attributes?.postalCode.slice(0, 2)})`
      : ''
  }`
  const startDate = data.attributes?.startDate ?? null
  const startDateFormatted = formatStartDate(startDate)
  const min = data?.attributes?.payRange?.min ?? undefined
  const max = data?.attributes?.payRange?.max ?? undefined
  const payRangeFormatted = formatSalary({ min, max }) ?? null
  const contractType =
    typeof data.attributes?.contractType === 'string'
      ? data.attributes?.contractType
      : ''
  const contractTypeFormatted = formatContractType(contractType) ?? null

  const medias =
    data.attributes.office?.data?.attributes?.firm?.data?.attributes?.medias ??
    null
  let offerMedias = null

  if (medias && medias.length > 0) {
    offerMedias = medias.map((media) => {
      if (media?.local?.data?.attributes?.url) {
        return addDomainOnUrl(media.local.data.attributes.url)
      }
      return null
    }) as string[] | null
  }

  offerMedias = offerMedias
    ? offerMedias.filter((media) => media !== null)
    : null

  const offerTools = data.attributes?.accountingTools?.data?.map((tool) => {
    const toolSlug =
      tool.attributes.permalink?.split('/').pop() || tool.attributes.permalink
    return {
      name: tool.attributes.name || null,
      logo:
        addDomainOnUrl(
          tool?.attributes?.logo?.data?.attributes?.logo?.data?.attributes
            ?.formats?.thumbnail?.url,
        ) || null,
      logoUrl:
        addDomainOnUrl(tool?.attributes?.logo?.data?.attributes?.url) || null,
      slug: toolSlug || null,
    }
  })

  if (data) {
    offer = {
      id: data.attributes.uuid,
      title: data.attributes.title,
      slug: data.attributes.slug,
      descriptions: {
        missions: data.attributes.description.missions || null,
        needs: data.attributes.description.needs || null,
        prospects: data.attributes.description.prospects || null,
        workWithUs: data.attributes.description.workWithUs || null,
        process: data.attributes.description.process || null,
      },
      positionFilled: false,
      publishedDate: formattedPublishedDate,
      startDate: startDateFormatted,
      educationRequirement: data.attributes.educationRequirement || null,
      experienceRequirement: data.attributes.experienceRequirement || null,
      contractType: contractTypeFormatted || null,
      payRange: payRangeFormatted,
      fullyRemoteJob: data.attributes.fullyRemoteJob ? 'Full remote' : null,
      hoursPerWeek: formatHoursPerWeek(data.attributes.hoursPerWeek || null),
      office: {
        city: formatedCity,
        cityForMap: data.attributes.office?.data?.attributes?.city || null,
        lat: data.attributes.office?.data?.attributes?.latitude || null,
        street: data.attributes.office?.data?.attributes?.street || null,
        postalCode:
          data.attributes.office?.data?.attributes?.postalCode || null,
        lng: data.attributes.office?.data?.attributes?.longitude || null,
        firm: {
          slug:
            data.attributes.office?.data?.attributes?.firm?.data?.attributes
              ?.slug || null,
          name:
            data.attributes.office?.data?.attributes?.firm?.data?.attributes
              ?.name || null,
          logo:
            addDomainOnUrl(
              data.attributes.office?.data?.attributes?.firm?.data?.attributes
                ?.logo?.data?.attributes?.url,
            ) || null,
          header:
            addDomainOnUrl(
              data.attributes.office?.data?.attributes?.firm?.data?.attributes
                ?.header?.data?.attributes?.url,
            ) || null,
          offerMedias: offerMedias?.slice(0, 4) || null,
          hasCareerSite:
            data.attributes.office?.data?.attributes?.firm?.data?.attributes
              ?.hasCareerSite || null,
          colorCareerSite:
            data.attributes.office?.data?.attributes?.firm?.data?.attributes
              ?.colorCareerSite || null,
        },
      },
      tools: offerTools || null,
    }
  }

  return offer as Offer | null
}

export type GoogleForJobs = {
  id: string | null
  title: string | null
  publishedDate: string | null
  description: string | null
  contractType: string | null
  firmName: string | null
  sameAs: string | null
  firmLogo: string | null
  street: string | null
  city: string | null
  postalCode: string | null
  salarymin: number | null
  salarymax: number | null
}

export function GoogleForJobsMapper(
  data: Queries.OfferQuery,
): GoogleForJobs | null {
  let dataToReturn = null
  const url = `https://lamacompta/cabinets/${data.strapiAccountingOffer?.office?.firm?.slug}/offres/${data.strapiAccountingOffer?.slug}`

  dataToReturn = {
    id: data.strapiAccountingOffer?.uuid || null,
    title: data.strapiAccountingOffer?.title || null,
    publishedDate: data.strapiAccountingOffer?.publishedAt || null,
    description:
      data.strapiAccountingOffer?.description?.missions?.data?.missions || null,
    contractType: data.strapiAccountingOffer?.contractType || null,
    firmName: data.strapiAccountingOffer?.office?.firm?.name || null,
    sameAs: url,
    firmLogo:
      data.strapiAccountingOffer?.office?.firm?.logo?.localFile?.url || null,
    street: data.strapiAccountingOffer?.office?.street || null,
    city: data.strapiAccountingOffer?.office?.city || null,
    postalCode: data.strapiAccountingOffer?.office?.postalCode || null,
    salarymin: formatRangeForGoogleJobs(
      data.strapiAccountingOffer?.payRange?.min,
    ),
    salarymax: formatRangeForGoogleJobs(
      data.strapiAccountingOffer?.payRange?.max,
    ),
  }
  return dataToReturn
}
